// PaymentsScreen.js — Manual e-Transfer reconciliation workspace
//
// Three-step flow surfaced at the top so the operator always knows where they
// are: (1) pick a pending payment, (2) select one or more deposits whose total
// equals the expected amount, (3) confirm. The right column auto-suggests any
// single deposit that already matches the expected amount; when no single
// deposit matches, the operator multi-selects deposits and the live tally
// shows when the sum hits the target. Server is the source of truth for the
// amount equality check; the client only previews the result.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getPendingEtransfers,
  getUnmatchedInbox,
  manualConfirmEtransfer,
} from "./adminService";
import "./PaymentsScreen.css";

// ─── Inline SVG icon set (Lucide-derived 1.75px stroke) ─────────────────────
const Icon = ({ d, size = 16, viewBox = "0 0 24 24", ...rest }) => (
  <svg
    width={size}
    height={size}
    viewBox={viewBox}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
    {...rest}
  >
    {d}
  </svg>
);
const IconClock     = (p) => <Icon {...p} d={<><circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15 14" /></>} />;
const IconHourglass = (p) => <Icon {...p} d={<><path d="M6 3h12M6 21h12M7 3v3a5 5 0 0 0 10 0V3M7 21v-3a5 5 0 0 1 10 0v3" /></>} />;
const IconMail      = (p) => <Icon {...p} d={<><rect x="3" y="5" width="18" height="14" rx="2" /><polyline points="3 7 12 13 21 7" /></>} />;
const IconUser      = (p) => <Icon {...p} d={<><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></>} />;
const IconBriefcase = (p) => <Icon {...p} d={<><rect x="3" y="7" width="18" height="13" rx="2" /><path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" /></>} />;
const IconRefresh   = (p) => <Icon {...p} d={<><polyline points="3 4 3 10 9 10" /><polyline points="21 20 21 14 15 14" /><path d="M5 10a8 8 0 0 1 14-3M19 14a8 8 0 0 1-14 3" /></>} />;
const IconSearch    = (p) => <Icon {...p} d={<><circle cx="11" cy="11" r="7" /><line x1="16.5" y1="16.5" x2="21" y2="21" /></>} />;
const IconCheck     = (p) => <Icon {...p} d={<><polyline points="20 6 9 17 4 12" /></>} />;
const IconCheckCircle = (p) => <Icon {...p} d={<><circle cx="12" cy="12" r="9" /><polyline points="8 12.5 11 15.5 16 9.5" /></>} />;
const IconAlert     = (p) => <Icon {...p} d={<><path d="M12 3 2 21h20Z" /><line x1="12" y1="10" x2="12" y2="14" /><circle cx="12" cy="17.5" r="0.6" fill="currentColor" /></>} />;
const IconX         = (p) => <Icon {...p} d={<><line x1="6" y1="6" x2="18" y2="18" /><line x1="18" y1="6" x2="6" y2="18" /></>} />;
const IconArrowRight= (p) => <Icon {...p} d={<><line x1="4" y1="12" x2="20" y2="12" /><polyline points="14 6 20 12 14 18" /></>} />;
const IconSparkles  = (p) => <Icon {...p} d={<><path d="M12 4v4M12 16v4M4 12h4M16 12h4M6 6l2.5 2.5M15.5 15.5 18 18M18 6l-2.5 2.5M8.5 15.5 6 18" /></>} />;
const IconInbox     = (p) => <Icon {...p} d={<><polyline points="3 13 8 13 10 16 14 16 16 13 21 13" /><path d="M5 7h14l2 6v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-6Z" /></>} />;
const IconLayers    = (p) => <Icon {...p} d={<><polygon points="12 2 22 8 12 14 2 8 12 2" /><polyline points="2 14 12 20 22 14" /><polyline points="2 19 12 25 22 19" /></>} />;
const IconPlus      = (p) => <Icon {...p} d={<><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></>} />;
const IconChevron   = (p) => <Icon {...p} d={<><polyline points="6 9 12 15 18 9" /></>} />;
const IconExternal  = (p) => <Icon {...p} d={<><path d="M14 4h6v6" /><line x1="20" y1="4" x2="11" y2="13" /><path d="M19 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h6" /></>} />;

// ─── Data helpers ──────────────────────────────────────────────────────────
const INBOX_RESULT_META = {
  no_code:         { label: "No code",         tone: "warn",  hint: "Email arrived without a recognisable reference code." },
  not_found:       { label: "Code not found",  tone: "info",  hint: "Reference code on the deposit doesn't match any pending payment." },
  amount_mismatch: { label: "Amount mismatch", tone: "muted", hint: "Reference matched a payment but the deposit amount is different." },
};
const inboxMeta = (r) =>
  INBOX_RESULT_META[r] || { label: r || "Unknown", tone: "muted", hint: "" };

const apiErrorMessage = (err, fallback) => {
  const data = err?.response?.data;
  if (typeof data === "string") return data;
  if (data?.error) return data.error;
  return err?.message || fallback;
};

const formatCents = (cents) => {
  if (cents === null || cents === undefined || Number.isNaN(Number(cents))) return "—";
  return `$${(Number(cents) / 100).toFixed(2)}`;
};

const formatTimestamp = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, {
    year: "numeric", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

const relativeTime = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const diffSec = Math.round((Date.now() - d.getTime()) / 1000);
  const past = diffSec >= 0;
  const abs = Math.abs(diffSec);
  if (abs < 60) return past ? "moments ago" : "in moments";
  const mins = Math.round(abs / 60);
  if (mins < 60) return past ? `${mins}m ago` : `in ${mins}m`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return past ? `${hrs}h ago` : `in ${hrs}h`;
  const days = Math.round(hrs / 24);
  return past ? `${days}d ago` : `in ${days}d`;
};

const fullName = (p) => {
  const f = (p.first_name || "").trim();
  const l = (p.last_name || "").trim();
  const joined = `${f} ${l}`.trim();
  return joined || p.client_email || `Account #${p.account_id}`;
};

const STEPS = [
  { id: 1, label: "Select a pending payment",        hint: "Left column" },
  { id: 2, label: "Pick deposits that total exactly", hint: "Right column · sum to expected" },
  { id: 3, label: "Confirm",                          hint: "Bottom action bar" },
];

// ─── Toast ─────────────────────────────────────────────────────────────────
function Toast({ message, tone = "success", onDismiss }) {
  useEffect(() => {
    if (!message) return undefined;
    const t = setTimeout(onDismiss, 5500);
    return () => clearTimeout(t);
  }, [message, onDismiss]);
  if (!message) return null;
  return (
    <div className={`pay-toast pay-toast--${tone}`} role="status" aria-live="polite">
      <span className="pay-toast__icon">
        {tone === "success" ? <IconCheckCircle size={20} /> : <IconAlert size={20} />}
      </span>
      <span className="pay-toast__message">{message}</span>
      <button
        type="button"
        className="pay-toast__close"
        onClick={onDismiss}
        aria-label="Dismiss notification"
      >
        <IconX size={16} />
      </button>
    </div>
  );
}

// ─── Confirmation modal — supports 1..N deposits ───────────────────────────
function ConfirmModal({ open, pending, deposits, onCancel, onConfirm, busy, errorText }) {
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const previouslyFocused = document.activeElement;
    const handleKey = (e) => {
      if (e.key === "Escape" && !busy) onCancel();
    };
    document.addEventListener("keydown", handleKey);
    const focusTarget = dialogRef.current?.querySelector(
      "[data-autofocus], button:not([disabled])"
    );
    focusTarget?.focus();
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
      previouslyFocused?.focus?.();
    };
  }, [open, busy, onCancel]);

  if (!open || !pending || !deposits || deposits.length === 0) return null;

  const sumCents = deposits.reduce((acc, d) => acc + Number(d.parsed_amount), 0);
  const isAggregate = deposits.length > 1;

  return (
    <div className="pay-modal-scrim" role="presentation" onClick={busy ? undefined : onCancel}>
      <div
        ref={dialogRef}
        className="pay-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pay-modal-title"
        aria-describedby="pay-modal-desc"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pay-modal__header">
          <h2 id="pay-modal-title" className="pay-modal__title">
            <IconCheckCircle size={20} />
            {isAggregate
              ? `Confirm ${deposits.length}-deposit reconciliation`
              : "Confirm manual reconciliation"}
          </h2>
          <button
            type="button"
            className="pay-iconbtn"
            onClick={onCancel}
            disabled={busy}
            aria-label="Close"
          >
            <IconX size={18} />
          </button>
        </div>

        <p id="pay-modal-desc" className="pay-modal__desc">
          This marks <strong>Booking #{pending.booking_id}</strong> as paid via
          {" "}{isAggregate ? `${deposits.length} aggregated e-Transfers` : "e-Transfer"}.
          The action is logged with your admin account.
        </p>

        <div className="pay-modal__paymentCard">
          <div className="pay-modal__cardLabel">Pending payment</div>
          <div className="pay-modal__cardTitle">{fullName(pending)}</div>
          <div className="pay-modal__cardSub">
            Booking #{pending.booking_id} · {pending.care_service_name || "Service"}
          </div>
          <div className="pay-modal__cardRow">
            <code className="pay-code">{pending.reference_code}</code>
            <span className="pay-modal__amount">{formatCents(pending.expected_amount)}</span>
          </div>
        </div>

        <div className="pay-modal__arrowMid" aria-hidden="true">
          <IconArrowRight size={20} />
        </div>

        <div className="pay-modal__depositList">
          <div className="pay-modal__depositHeader">
            <span className="pay-modal__cardLabel">
              {isAggregate ? `Bank deposits (${deposits.length})` : "Bank deposit"}
            </span>
            <span className="pay-modal__depositTotal">
              Total {formatCents(sumCents)}
            </span>
          </div>
          <ul className="pay-modal__deposits">
            {deposits.map((d) => (
              <li key={d.gmail_message_id} className="pay-modal__deposit">
                <div className="pay-modal__depositMain">
                  <div className="pay-modal__depositSender">
                    {d.parsed_sender_name || d.sender || "Unknown sender"}
                  </div>
                  <div className="pay-modal__depositMeta">
                    <code className="pay-code pay-code--sm">{d.parsed_code || "no code"}</code>
                    <span>· seen {relativeTime(d.processed_at)}</span>
                  </div>
                </div>
                <div className="pay-modal__depositAmount">{formatCents(d.parsed_amount)}</div>
              </li>
            ))}
          </ul>
        </div>

        {errorText && (
          <div className="pay-banner pay-banner--error" role="alert">
            <IconAlert size={16} /> <span>{errorText}</span>
          </div>
        )}

        <div className="pay-modal__actions">
          <button type="button" className="pay-btn pay-btn--ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="pay-btn pay-btn--primary"
            data-autofocus
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? (
              <><span className="pay-spinner" aria-hidden="true" /> Confirming…</>
            ) : (
              <><IconCheck size={16} /> Mark as paid</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main screen ───────────────────────────────────────────────────────────
function PaymentsScreen() {
  const [pendingRows, setPendingRows] = useState([]);
  const [inboxRows, setInboxRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [refresh, setRefresh] = useState(0);

  const [selectedPendingId, setSelectedPendingId] = useState(null);
  // Set<gmail_message_id> — multi-select for aggregate reconciliation
  const [selectedInboxIds, setSelectedInboxIds] = useState(() => new Set());

  const [pendingSearch, setPendingSearch] = useState("");
  const [inboxFilter, setInboxFilter] = useState("all");
  const [inboxSearch, setInboxSearch] = useState("");
  const [expandedInboxId, setExpandedInboxId] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState("");
  const [toast, setToast] = useState(null);

  const token = () => localStorage.getItem("@uphealth_login_token");

  const loadAll = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const [pendingRes, inboxRes] = await Promise.all([
        getPendingEtransfers(token()),
        getUnmatchedInbox(token()),
      ]);
      setPendingRows(pendingRes?.data?.data || []);
      setInboxRows(inboxRes?.data?.data || []);
    } catch (err) {
      setPendingRows([]);
      setInboxRows([]);
      setLoadError(apiErrorMessage(err, "Could not load reconciliation queue."));
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll, refresh]);

  const selectedPending = useMemo(
    () => pendingRows.find((r) => r.etransfer_id === selectedPendingId) || null,
    [pendingRows, selectedPendingId],
  );

  const selectedInbox = useMemo(
    () => inboxRows.filter((r) => selectedInboxIds.has(r.gmail_message_id)),
    [inboxRows, selectedInboxIds],
  );

  const depositSumCents = useMemo(
    () => selectedInbox.reduce((acc, r) => acc + Number(r.parsed_amount || 0), 0),
    [selectedInbox],
  );

  const expectedCents = selectedPending ? Number(selectedPending.expected_amount) : 0;
  const remainingCents = selectedPending ? expectedCents - depositSumCents : 0;

  const tallyState = useMemo(() => {
    if (!selectedPending || selectedInbox.length === 0) return "idle";
    if (depositSumCents === expectedCents) return "match";
    if (depositSumCents > expectedCents) return "over";
    return "under";
  }, [selectedPending, selectedInbox, depositSumCents, expectedCents]);

  // KPI cards
  const stats = useMemo(() => {
    const totalCents = pendingRows.reduce((s, r) => s + Number(r.expected_amount || 0), 0);
    const oldest = pendingRows.reduce((earliest, r) => {
      const t = new Date(r.created_at).getTime();
      return Number.isFinite(t) && (earliest === null || t < earliest) ? t : earliest;
    }, null);
    return {
      pendingCount: pendingRows.length,
      pendingTotal: totalCents,
      inboxCount: inboxRows.length,
      oldestPending: oldest ? new Date(oldest).toISOString() : null,
    };
  }, [pendingRows, inboxRows]);

  // Filtering
  const filteredPending = useMemo(() => {
    const q = pendingSearch.trim().toLowerCase();
    if (!q) return pendingRows;
    return pendingRows.filter((r) => {
      const hay = [
        r.reference_code, r.first_name, r.last_name, r.client_email,
        r.care_service_name, r.provider_name, String(r.booking_id),
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [pendingRows, pendingSearch]);

  const filteredInbox = useMemo(() => {
    const q = inboxSearch.trim().toLowerCase();
    return inboxRows.filter((r) => {
      if (inboxFilter !== "all" && r.match_result !== inboxFilter) return false;
      if (!q) return true;
      const hay = [r.sender, r.parsed_code, r.gmail_message_id]
        .filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [inboxRows, inboxFilter, inboxSearch]);

  // Auto-suggest single-deposit candidates
  const suggestedInboxIds = useMemo(() => {
    if (!selectedPending) return new Set();
    const target = Number(selectedPending.expected_amount);
    return new Set(
      inboxRows
        .filter((r) => Number(r.parsed_amount) === target)
        .map((r) => r.gmail_message_id),
    );
  }, [selectedPending, inboxRows]);

  // When operator has selected deposits but no payment, suggest pending rows
  // whose expected_amount === sum of selected deposits.
  const suggestedPendingIds = useMemo(() => {
    if (selectedInbox.length === 0) return new Set();
    const target = depositSumCents;
    return new Set(
      pendingRows
        .filter((r) => Number(r.expected_amount) === target)
        .map((r) => r.etransfer_id),
    );
  }, [selectedInbox, depositSumCents, pendingRows]);

  const currentStep = useMemo(() => {
    if (!selectedPending) return 1;
    if (tallyState !== "match") return 2;
    return 3;
  }, [selectedPending, tallyState]);

  const inboxCounts = useMemo(() => {
    const c = { all: inboxRows.length, no_code: 0, not_found: 0, amount_mismatch: 0 };
    inboxRows.forEach((r) => { if (c[r.match_result] !== undefined) c[r.match_result]++; });
    return c;
  }, [inboxRows]);

  const toggleInbox = (id) => {
    setConfirmError("");
    setSelectedInboxIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedPendingId(null);
    setSelectedInboxIds(new Set());
    setConfirmError("");
  };

  const clearDeposits = () => {
    setSelectedInboxIds(new Set());
    setConfirmError("");
  };

  const openConfirm = () => {
    if (!selectedPending || selectedInbox.length === 0 || tallyState !== "match") return;
    setConfirmError("");
    setModalOpen(true);
  };

  const handleConfirm = async () => {
    if (!selectedPending || selectedInbox.length === 0) return;
    setConfirming(true);
    setConfirmError("");
    try {
      const ids = selectedInbox.map((r) => r.gmail_message_id);
      const { data } = await manualConfirmEtransfer(
        token(),
        selectedPending.etransfer_id,
        ids,
      );
      setToast({
        tone: "success",
        message:
          data.deposit_count > 1
            ? `Booking #${data.booking_id} marked paid using ${data.deposit_count} deposits (${formatCents(data.total_applied)}).`
            : `Booking #${data.booking_id} marked paid via manual e-Transfer confirmation.`,
      });
      setModalOpen(false);
      clearSelection();
      setRefresh((n) => n + 1);
    } catch (err) {
      const d = err?.response?.data;
      if (d?.error === "amount_mismatch") {
        setConfirmError(
          `Total mismatch — expected ${formatCents(d.expected)}, received ${formatCents(d.received)} across ${d.deposit_count} deposit(s).`,
        );
      } else if (d?.error === "not_pending") {
        setConfirmError(`That e-Transfer is no longer pending (status: ${d.status}). The list will refresh.`);
        setRefresh((n) => n + 1);
      } else if (d?.error === "inbox_not_unmatched") {
        const ids = (d.ineligible || []).map((x) => x.gmail_message_id).join(", ");
        setConfirmError(`One or more deposits are no longer unmatched (${ids}). The list will refresh.`);
        setRefresh((n) => n + 1);
      } else if (d?.error === "deposit_already_applied") {
        setConfirmError(`A selected deposit was already applied to another payment. The list will refresh.`);
        setRefresh((n) => n + 1);
      } else if (d?.error === "too_many_deposits") {
        setConfirmError(`Too many deposits selected (max ${d.max}). Deselect some and retry.`);
      } else {
        setConfirmError(apiErrorMessage(err, "Could not confirm payment."));
      }
    }
    setConfirming(false);
  };

  // Keyboard support on the panels. Pending = single-select (arrow moves
  // selection). Inbox = multi-select (arrow moves an active cursor; Space
  // toggles). For simplicity the inbox arrow keys also toggle selection on
  // move — operators can also click checkboxes.
  const onPanelKey = (panel) => (e) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    const rows = panel === "pending" ? filteredPending : filteredInbox;
    if (rows.length === 0) return;
    if (panel === "pending") {
      const idx = rows.findIndex((r) => r.etransfer_id === selectedPendingId);
      const nextIdx =
        idx === -1 ? 0
        : e.key === "ArrowDown" ? Math.min(rows.length - 1, idx + 1)
        : Math.max(0, idx - 1);
      setSelectedPendingId(rows[nextIdx].etransfer_id);
    }
    // Inbox arrow nav intentionally a no-op — selection is via mouse/checkbox
    // so arrow keys don't surprise-toggle items.
  };

  const stepFor = (id) =>
    id < currentStep ? "done" : id === currentStep ? "active" : "pending";

  const tallyPct = selectedPending && expectedCents > 0
    ? Math.min(100, (depositSumCents / expectedCents) * 100)
    : 0;

  return (
    <div className="pay-container">
      {/* ── Page Header ── */}
      <div className="page-header">
        <div>
          <h1 className="page-header__title">Payments Reconciliation</h1>
          <div className="page-header__subtitle">
            Manually confirm e-Transfer payments — one deposit or several that add up to the expected total.
          </div>
        </div>
        <div className="page-header__right">
          <button
            type="button"
            className="pay-btn pay-btn--ghost"
            onClick={() => setRefresh((n) => n + 1)}
            disabled={loading}
          >
            <IconRefresh size={16} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* ── Stepper ── */}
      <ol className="pay-stepper" aria-label="Reconciliation flow">
        {STEPS.map((s, idx) => {
          const state = stepFor(s.id);
          return (
            <li key={s.id} className={`pay-step pay-step--${state}`}>
              <div className="pay-step__bullet" aria-hidden="true">
                {state === "done" ? <IconCheck size={14} /> : s.id}
              </div>
              <div className="pay-step__label">
                <div className="pay-step__title">{s.label}</div>
                <div className="pay-step__hint">{s.hint}</div>
              </div>
              {idx < STEPS.length - 1 && (
                <span className="pay-step__connector" aria-hidden="true" />
              )}
            </li>
          );
        })}
      </ol>

      {/* ── KPI strip ── */}
      <div className="pay-stats" role="list">
        <div className="pay-stat" role="listitem">
          <div className="pay-stat__icon pay-stat__icon--teal"><IconHourglass size={18} /></div>
          <div className="pay-stat__body">
            <div className="pay-stat__value">
              {loading ? <span className="pay-skel pay-skel--lg" /> : stats.pendingCount}
            </div>
            <div className="pay-stat__label">Pending payments</div>
          </div>
        </div>
        <div className="pay-stat" role="listitem">
          <div className="pay-stat__icon pay-stat__icon--violet"><IconBriefcase size={18} /></div>
          <div className="pay-stat__body">
            <div className="pay-stat__value">
              {loading ? <span className="pay-skel pay-skel--lg" /> : formatCents(stats.pendingTotal)}
            </div>
            <div className="pay-stat__label">Total owed</div>
          </div>
        </div>
        <div className="pay-stat" role="listitem">
          <div className="pay-stat__icon pay-stat__icon--blue"><IconInbox size={18} /></div>
          <div className="pay-stat__body">
            <div className="pay-stat__value">
              {loading ? <span className="pay-skel pay-skel--lg" /> : stats.inboxCount}
            </div>
            <div className="pay-stat__label">Unmatched deposits</div>
          </div>
        </div>
        <div className="pay-stat" role="listitem">
          <div className="pay-stat__icon pay-stat__icon--red"><IconClock size={18} /></div>
          <div className="pay-stat__body">
            <div className="pay-stat__value">
              {loading
                ? <span className="pay-skel pay-skel--lg" />
                : (stats.oldestPending ? relativeTime(stats.oldestPending) : "—")}
            </div>
            <div className="pay-stat__label">Oldest pending</div>
          </div>
        </div>
      </div>

      {loadError && (
        <div className="pay-banner pay-banner--error" role="alert">
          <IconAlert size={16} />
          <span>{loadError}</span>
          <button
            type="button"
            className="pay-btn pay-btn--ghost pay-btn--sm"
            onClick={() => setRefresh((n) => n + 1)}
          >
            Retry
          </button>
        </div>
      )}

      {/* ── Reconciliation grid ── */}
      <div className="pay-grid">
        {/* LEFT: pending payments */}
        <section
          className={`pay-panel ${selectedPending ? "pay-panel--has-selection" : ""}`}
          aria-label="Pending payments"
          onKeyDown={onPanelKey("pending")}
        >
          <header className="pay-panel__header">
            <div className="pay-panel__titleblock">
              <span className="pay-panel__step">Step 1</span>
              <h2 className="pay-panel__title">
                <IconHourglass size={16} />
                Pending payments
              </h2>
            </div>
            <span className="pay-pill pay-pill--neutral">{filteredPending.length}</span>
          </header>

          <div className="pay-panel__toolbar">
            <label className="pay-search">
              <IconSearch size={14} />
              <input
                type="search"
                value={pendingSearch}
                onChange={(e) => setPendingSearch(e.target.value)}
                placeholder="Search reference, client, booking…"
                aria-label="Search pending payments"
              />
              {pendingSearch && (
                <button
                  type="button"
                  className="pay-search__clear"
                  onClick={() => setPendingSearch("")}
                  aria-label="Clear search"
                >
                  <IconX size={14} />
                </button>
              )}
            </label>
          </div>

          <div className="pay-list" tabIndex={0} role="listbox" aria-label="Pending payments list">
            {loading && Array.from({ length: 3 }).map((_, i) => (
              <div key={`skel-${i}`} className="pay-row pay-row--skel" aria-hidden="true">
                <div className="pay-skel pay-skel--line" style={{ width: "60%" }} />
                <div className="pay-skel pay-skel--line" style={{ width: "40%" }} />
                <div className="pay-skel pay-skel--line" style={{ width: "75%" }} />
              </div>
            ))}

            {!loading && filteredPending.length === 0 && (
              <div className="pay-empty">
                <div className="pay-empty__icon"><IconCheckCircle size={28} /></div>
                <div className="pay-empty__title">
                  {pendingRows.length === 0 ? "Nothing pending" : "Nothing matches that search"}
                </div>
                <div className="pay-empty__desc">
                  {pendingRows.length === 0
                    ? "Gmail polling is handling deposits automatically. Manual confirmation isn't needed."
                    : "Try a different reference code or client name."}
                </div>
              </div>
            )}

            {!loading && filteredPending.map((row) => {
              const selected = row.etransfer_id === selectedPendingId;
              const suggested = !selected && suggestedPendingIds.has(row.etransfer_id);
              return (
                <button
                  key={row.etransfer_id}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={[
                    "pay-row",
                    selected && "pay-row--selected",
                    suggested && "pay-row--suggested",
                  ].filter(Boolean).join(" ")}
                  onClick={() => {
                    setSelectedPendingId(selected ? null : row.etransfer_id);
                    setConfirmError("");
                  }}
                >
                  <div className="pay-row__head">
                    <div className="pay-row__titleblock">
                      <span className="pay-row__title">{fullName(row)}</span>
                      <span className="pay-row__sub">
                        Booking #{row.booking_id} · {row.care_service_name || "Service"}
                      </span>
                    </div>
                    <span className="pay-row__amount">
                      {formatCents(row.expected_amount)}
                    </span>
                  </div>
                  <div className="pay-row__metarow">
                    <code className="pay-code" title="Reference code shown to client">
                      {row.reference_code}
                    </code>
                    {row.provider_name && (
                      <span className="pay-meta">
                        <IconUser size={12} /> {row.provider_name}
                      </span>
                    )}
                    <span className="pay-meta pay-meta--muted" title={formatTimestamp(row.created_at)}>
                      <IconClock size={12} /> {relativeTime(row.created_at)}
                    </span>
                    {suggested && (
                      <span className="pay-pill pay-pill--suggest">
                        <IconSparkles size={12} /> total matches
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        {/* RIGHT: unmatched deposits — multi-select */}
        <section
          className={`pay-panel ${selectedInbox.length > 0 ? "pay-panel--has-selection" : ""}`}
          aria-label="Unmatched deposits"
        >
          <header className="pay-panel__header">
            <div className="pay-panel__titleblock">
              <span className="pay-panel__step">Step 2</span>
              <h2 className="pay-panel__title">
                <IconMail size={16} />
                Unmatched deposits
                {selectedInbox.length > 0 && (
                  <span className="pay-panel__counter">
                    <IconLayers size={12} /> {selectedInbox.length} selected
                  </span>
                )}
              </h2>
            </div>
            <span className="pay-pill pay-pill--neutral">{filteredInbox.length}</span>
          </header>

          {/* Live tally bar — visible whenever pending is chosen */}
          {selectedPending && (
            <div className={`pay-tally pay-tally--${tallyState}`} aria-live="polite">
              <div className="pay-tally__row">
                <div className="pay-tally__label">
                  <IconLayers size={14} />
                  <span>Selected sum</span>
                </div>
                <div className="pay-tally__numbers">
                  <strong>{formatCents(depositSumCents)}</strong>
                  <span className="pay-tally__divider">/</span>
                  <span>{formatCents(expectedCents)}</span>
                </div>
              </div>
              <div className="pay-tally__bar" aria-hidden="true">
                <div className="pay-tally__fill" style={{ width: `${tallyPct}%` }} />
              </div>
              <div className="pay-tally__status">
                {tallyState === "idle" && (
                  <span className="pay-tally__hint">
                    Pick one deposit that matches the total, or multiple deposits that sum to it.
                  </span>
                )}
                {tallyState === "under" && (
                  <span className="pay-tally__hint">
                    Need {formatCents(remainingCents)} more.
                  </span>
                )}
                {tallyState === "over" && (
                  <span className="pay-tally__hint pay-tally__hint--warn">
                    <IconAlert size={12} /> Over by {formatCents(depositSumCents - expectedCents)} — deselect a deposit.
                  </span>
                )}
                {tallyState === "match" && (
                  <span className="pay-tally__hint pay-tally__hint--ok">
                    <IconCheckCircle size={12} /> Totals match — ready to confirm.
                  </span>
                )}
                {selectedInbox.length > 0 && (
                  <button
                    type="button"
                    className="pay-btn pay-btn--ghost pay-btn--sm pay-tally__clear"
                    onClick={clearDeposits}
                  >
                    Clear deposits
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="pay-panel__toolbar pay-panel__toolbar--stack">
            <label className="pay-search">
              <IconSearch size={14} />
              <input
                type="search"
                value={inboxSearch}
                onChange={(e) => setInboxSearch(e.target.value)}
                placeholder="Search sender, code, message id…"
                aria-label="Search unmatched deposits"
              />
              {inboxSearch && (
                <button
                  type="button"
                  className="pay-search__clear"
                  onClick={() => setInboxSearch("")}
                  aria-label="Clear search"
                >
                  <IconX size={14} />
                </button>
              )}
            </label>
            <div className="pay-chiprow" role="tablist" aria-label="Filter by reason">
              {[
                { id: "all",             label: "All" },
                { id: "no_code",         label: "No code" },
                { id: "not_found",       label: "Code not found" },
                { id: "amount_mismatch", label: "Amount mismatch" },
              ].map((c) => (
                <button
                  key={c.id}
                  type="button"
                  role="tab"
                  aria-selected={inboxFilter === c.id}
                  className={`pay-chip ${inboxFilter === c.id ? "pay-chip--active" : ""}`}
                  onClick={() => setInboxFilter(c.id)}
                >
                  {c.label}
                  <span className="pay-chip__count">{inboxCounts[c.id]}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="pay-list" role="listbox" aria-multiselectable="true" aria-label="Unmatched deposits list">
            {loading && Array.from({ length: 3 }).map((_, i) => (
              <div key={`skel-${i}`} className="pay-row pay-row--skel" aria-hidden="true">
                <div className="pay-skel pay-skel--line" style={{ width: "55%" }} />
                <div className="pay-skel pay-skel--line" style={{ width: "30%" }} />
                <div className="pay-skel pay-skel--line" style={{ width: "70%" }} />
              </div>
            ))}

            {!loading && filteredInbox.length === 0 && (
              <div className="pay-empty">
                <div className="pay-empty__icon"><IconInbox size={28} /></div>
                <div className="pay-empty__title">
                  {inboxRows.length === 0
                    ? "All deposits accounted for"
                    : "Nothing matches this filter"}
                </div>
                <div className="pay-empty__desc">
                  {inboxRows.length === 0
                    ? "No unmatched deposits in the last 30 days."
                    : "Adjust the filter chips or search above."}
                </div>
              </div>
            )}

            {!loading && filteredInbox.map((row) => {
              const selected = selectedInboxIds.has(row.gmail_message_id);
              const suggested = !selected && suggestedInboxIds.has(row.gmail_message_id);
              const expanded  = expandedInboxId === row.gmail_message_id;
              const meta      = inboxMeta(row.match_result);
              const displayName = row.parsed_sender_name || row.sender || "Unknown sender";
              const gmailLink = `https://mail.google.com/mail/u/0/#all/${encodeURIComponent(row.gmail_message_id)}`;
              return (
                <div
                  key={row.gmail_message_id}
                  role="option"
                  aria-selected={selected}
                  className={[
                    "pay-row",
                    "pay-row--checkable",
                    selected && "pay-row--selected",
                    suggested && "pay-row--suggested",
                    expanded && "pay-row--expanded",
                  ].filter(Boolean).join(" ")}
                >
                  <button
                    type="button"
                    className="pay-row__hit"
                    onClick={() => toggleInbox(row.gmail_message_id)}
                    aria-label={`${selected ? "Deselect" : "Select"} deposit from ${displayName} for ${formatCents(row.parsed_amount)}`}
                  >
                    <span
                      className={`pay-check ${selected ? "pay-check--on" : ""}`}
                      aria-hidden="true"
                    >
                      {selected ? <IconCheck size={12} /> : <IconPlus size={12} />}
                    </span>
                    <div className="pay-row__body">
                      <div className="pay-row__head">
                        <div className="pay-row__titleblock">
                          <span className="pay-row__title">{displayName}</span>
                          <span className="pay-row__sub">
                            <span className={`pay-pill pay-pill--${meta.tone}`} title={meta.hint}>
                              {meta.label}
                            </span>
                            {row.parsed_sender_name && row.sender && (
                              <span className="pay-meta pay-meta--muted">
                                via {row.sender}
                              </span>
                            )}
                          </span>
                        </div>
                        <span className="pay-row__amount">
                          {formatCents(row.parsed_amount)}
                        </span>
                      </div>
                      <div className="pay-row__metarow">
                        <code className="pay-code">{row.parsed_code || "no code"}</code>
                        <span className="pay-meta pay-meta--muted" title={formatTimestamp(row.processed_at)}>
                          <IconClock size={12} /> {relativeTime(row.processed_at)}
                        </span>
                        {suggested && (
                          <span className="pay-pill pay-pill--suggest">
                            <IconSparkles size={12} /> exact match
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                  <button
                    type="button"
                    className={`pay-row__expandBtn ${expanded ? "pay-row__expandBtn--on" : ""}`}
                    onClick={() => setExpandedInboxId(expanded ? null : row.gmail_message_id)}
                    aria-label={expanded ? "Hide deposit details" : "Show deposit details"}
                    aria-controls={`pay-detail-${row.gmail_message_id}`}
                  >
                    <IconChevron size={14} />
                  </button>
                  {expanded && (
                    <div
                      id={`pay-detail-${row.gmail_message_id}`}
                      className="pay-row__detail"
                    >
                      <dl className="pay-detail__grid">
                        <dt>Subject</dt>
                        <dd>{row.subject || <span className="pay-detail__muted">(no subject)</span>}</dd>

                        <dt>Sender (parsed)</dt>
                        <dd>{row.parsed_sender_name || <span className="pay-detail__muted">(could not extract)</span>}</dd>

                        <dt>From header</dt>
                        <dd className="pay-detail__mono">{row.sender || <span className="pay-detail__muted">(unknown)</span>}</dd>

                        <dt>Received</dt>
                        <dd title={formatTimestamp(row.processed_at)}>
                          {formatTimestamp(row.processed_at) || "—"}
                        </dd>

                        <dt>Gmail id</dt>
                        <dd className="pay-detail__mono">{row.gmail_message_id}</dd>

                        <dt>Body excerpt</dt>
                        <dd className="pay-detail__body">
                          {row.body_snippet
                            ? row.body_snippet
                            : <span className="pay-detail__muted">No body captured.</span>}
                        </dd>
                      </dl>
                      <div className="pay-detail__actions">
                        <a
                          href={gmailLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="pay-btn pay-btn--ghost pay-btn--sm"
                        >
                          <IconExternal size={14} /> Open in Gmail
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      </div>

      {/* ── Sticky action bar ── */}
      <div
        className={[
          "pay-actionbar",
          (selectedPending || selectedInbox.length > 0) && "pay-actionbar--visible",
        ].filter(Boolean).join(" ")}
        aria-hidden={!(selectedPending || selectedInbox.length > 0)}
      >
        <div className="pay-actionbar__inner">
          <div className="pay-actionbar__cards">
            <div className={`pay-actionbar__card ${selectedPending ? "" : "pay-actionbar__card--empty"}`}>
              <div className="pay-actionbar__cardLabel">Payment</div>
              {selectedPending ? (
                <>
                  <div className="pay-actionbar__cardTitle">
                    Booking #{selectedPending.booking_id} · {fullName(selectedPending)}
                  </div>
                  <div className="pay-actionbar__cardSub">
                    <code className="pay-code pay-code--sm">{selectedPending.reference_code}</code>
                    <span className="pay-actionbar__amount">
                      {formatCents(selectedPending.expected_amount)}
                    </span>
                  </div>
                </>
              ) : (
                <div className="pay-actionbar__placeholder">Pick a pending payment on the left</div>
              )}
            </div>

            <div className="pay-actionbar__arrow" aria-hidden="true">
              <IconArrowRight size={20} />
            </div>

            <div className={`pay-actionbar__card ${selectedInbox.length > 0 ? "" : "pay-actionbar__card--empty"}`}>
              <div className="pay-actionbar__cardLabel">
                {selectedInbox.length > 1 ? `Deposits (${selectedInbox.length})` : "Deposit"}
              </div>
              {selectedInbox.length > 0 ? (
                <>
                  <div className="pay-actionbar__cardTitle">
                    {selectedInbox.length === 1
                      ? (selectedInbox[0].parsed_sender_name || selectedInbox[0].sender || "Unknown sender")
                      : `Aggregating ${selectedInbox.length} deposits`}
                  </div>
                  <div className="pay-actionbar__cardSub">
                    {selectedInbox.length === 1 ? (
                      <code className="pay-code pay-code--sm">
                        {selectedInbox[0].parsed_code || "no code"}
                      </code>
                    ) : (
                      <span className="pay-actionbar__multiHint">
                        From {new Set(selectedInbox.map((r) => r.sender || "—")).size} sender(s)
                      </span>
                    )}
                    <span className="pay-actionbar__amount">
                      {formatCents(depositSumCents)}
                    </span>
                  </div>
                </>
              ) : (
                <div className="pay-actionbar__placeholder">Pick one or more deposits on the right</div>
              )}
            </div>
          </div>

          <div className="pay-actionbar__status" aria-live="polite">
            {selectedPending && tallyState === "match" && (
              <span className="pay-pill pay-pill--success">
                <IconCheckCircle size={14} /> Totals match
              </span>
            )}
            {selectedPending && tallyState === "under" && (
              <span className="pay-pill pay-pill--info">
                Need {formatCents(remainingCents)} more
              </span>
            )}
            {selectedPending && tallyState === "over" && (
              <span className="pay-pill pay-pill--warn">
                <IconAlert size={14} /> Over by {formatCents(depositSumCents - expectedCents)}
              </span>
            )}
          </div>

          <div className="pay-actionbar__actions">
            <button
              type="button"
              className="pay-btn pay-btn--ghost"
              onClick={clearSelection}
              disabled={confirming}
            >
              Clear
            </button>
            <button
              type="button"
              className="pay-btn pay-btn--primary"
              onClick={openConfirm}
              disabled={!selectedPending || selectedInbox.length === 0 || tallyState !== "match"}
              title={
                !selectedPending ? "Pick a pending payment"
                : selectedInbox.length === 0 ? "Pick at least one deposit"
                : tallyState !== "match" ? "Total must equal expected amount"
                : "Open confirmation dialog"
              }
            >
              <IconCheck size={16} /> Review &amp; confirm
            </button>
          </div>
        </div>
      </div>

      <ConfirmModal
        open={modalOpen}
        pending={selectedPending}
        deposits={selectedInbox}
        busy={confirming}
        errorText={confirmError}
        onCancel={() => { if (!confirming) { setModalOpen(false); setConfirmError(""); } }}
        onConfirm={handleConfirm}
      />

      <Toast
        message={toast?.message}
        tone={toast?.tone}
        onDismiss={() => setToast(null)}
      />
    </div>
  );
}

export default PaymentsScreen;
